import React from "react";
import { Box, IconButton, TextField, Tooltip } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { format as formatDate, isValid as isValidDate } from "date-fns";

/**
 * material-table column filter for date/timestamp columns. Renders From /
 * To native date inputs so users can type or pick a date directly instead
 * of paging a calendar month-by-month. Either bound may be left blank:
 * only From → everything on/after it, only To → everything on/before it.
 *
 * The filter value stored on the column is { from, to } as "yyyy-MM-dd"
 * strings. Pair with makeDateRangeFilter(field) as customFilterAndSearch.
 */
const DateRangeColumnFilter = ({ columnDef, onFilterChanged }) => {
  const value = columnDef.tableData.filterValue || {};

  const handleChange = (key) => (e) => {
    const next = { ...value, [key]: e.target.value || undefined };
    const isEmpty = !next.from && !next.to;
    onFilterChanged(columnDef.tableData.id, isEmpty ? undefined : next);
  };

  const handleClear = () => {
    onFilterChanged(columnDef.tableData.id, undefined);
  };

  return (
    <Box display="flex" alignItems="center" gridGap={4}>
      <Box display="flex" flexDirection="column" gridGap={4}>
        <TextField
          type="date"
          value={value.from || ""}
          onChange={handleChange("from")}
          inputProps={{ "aria-label": `${columnDef.title} from` }}
          title="From (leave blank for no lower bound)"
        />
        <TextField
          type="date"
          value={value.to || ""}
          onChange={handleChange("to")}
          inputProps={{ "aria-label": `${columnDef.title} to` }}
          title="To (leave blank for no upper bound)"
        />
      </Box>
      {(value.from || value.to) && (
        <Tooltip title="Clear date filter">
          <IconButton size="small" onClick={handleClear}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * Build a customFilterAndSearch fn matching DateRangeColumnFilter's
 * { from, to } value against rowData[field]. Bounds are inclusive and
 * compared by local calendar day, so a To date includes that whole day.
 */
export const makeDateRangeFilter = (field) => (filterValue, rowData) => {
  const { from, to } = filterValue || {};
  if (!from && !to) return true;
  const raw = rowData[field];
  if (!raw) return false;
  const d = new Date(raw);
  if (!isValidDate(d)) return false;
  const day = formatDate(d, "yyyy-MM-dd");
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};

export default DateRangeColumnFilter;
